"use client";

// "Live" nav item. A green pulse + "Live", linking to /live; when players are
// actually online it appends the live count. Polls /api/presence/active.
//
// Self-contained on purpose: the navbar mounts on every page, so this keeps
// its own tiny poll rather than pulling in the live-page module chain.
import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { Link } from "@/i18n/navigation";
import { useT } from "@/lib/i18n";
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
// Gentle: this runs on every page for every visitor, and a live count does
// not need to be second-accurate.
const POLL_MS = 60_000;
function LiveCircle() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="relative flex h-2 w-2 shrink-0"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--color-silent)] opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--color-silent)]" /></span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export default function LiveNavButton({
  variant = "desktop"
}: {
  variant?: "desktop" | "mobile";
}) {
  const t = useT();
  const [count, setCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    async function poll() {
      if (document.hidden) return;
      try {
        const r = await fetch(`${API}/api/presence/active`);
        if (!r.ok) return;
        const d = await r.json();
        const n = typeof d.count === "number" ? d.count : d.players?.length ?? 0;
        if (!cancelled) setCount(n);
      } catch {
        // Leave the last known count; the next beat recovers.
      }
    }
    poll();
    const t_0 = setInterval(poll, POLL_MS);
    const onVis = () => {
      if (!document.hidden) poll();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      clearInterval(t_0);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);
  if (variant === "mobile") {
    return <Link prefetch={false} href="/live" className="flex items-center gap-2 text-lg font-semibold text-[var(--color-silent)]">
        <LiveCircle />
        <span className="tabular-nums">
          {count > 0 ? t("{n} Live", {
          n: count
        }) : t("Live")}
        </span>
      </Link>;
  }
  return <Link prefetch={false} href="/live" title={t("Watch players live")} className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-[var(--color-silent)] hover:bg-[var(--bg-card)] transition-colors shrink-0">
      <LiveCircle />
      <span className="tabular-nums">
        {count > 0 ? t("{n} Live", {
        n: count
      }) : t("Live")}
      </span>
    </Link>;
}